import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Wordmark from "../components/Wordmark";
import GoldDivider from "../components/GoldDivider";
import Reveal from "../components/Reveal";
import PropertyCard from "../components/PropertyCard";
import { properties } from "../data/properties";
import { services } from "../data/services";

const HERO_IMG =
    "https://images.pexels.com/photos/33466000/pexels-photo-33466000.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1800";

const Home = () => {
    const [scrollY, setScrollY] = useState(0);

    useEffect(() => {
        const onScroll = () => setScrollY(window.scrollY);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const featured = [properties[2], properties[0], properties[9]];
    const commercial = properties.filter((p) => p.category === "commercial").slice(0, 3);
    const residential = properties.filter((p) => p.category === "residential").slice(0, 3);

    return (
        <div data-testid="page-home">
            {/* HERO */}
            <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
                <div
                    className="absolute inset-0 will-change-transform"
                    style={{
                        transform: `translate3d(0, ${scrollY * 0.3}px, 0) scale(1.08)`,
                    }}
                >
                    <img
                        src={HERO_IMG}
                        alt="Las Vegas skyline at night"
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black" />

                <div className="relative z-10 text-center px-6 max-w-5xl mx-auto pt-32 pb-20">
                    <Reveal>
                        <span className="inline-block text-[0.65rem] sm:text-xs tracking-[0.55em] uppercase text-brand-silver/70 border-y border-brand-gold/40 py-2 px-6">
                            Est. Las Vegas · Private Brokerage
                        </span>
                    </Reveal>
                    <Reveal delay={200}>
                        <div className="mt-12">
                            <Wordmark size="xl" subtitle />
                        </div>
                    </Reveal>
                    <Reveal delay={500}>
                        <p className="mt-10 text-brand-silver/85 text-lg md:text-xl font-serif-editorial italic max-w-2xl mx-auto leading-relaxed">
                            A private brokerage for commercial assets and
                            multi&#8209;million&#8209;dollar estates.
                            Surgical. Discreet. Without compromise.
                        </p>
                    </Reveal>
                    <Reveal delay={750}>
                        <div className="mt-12 flex flex-col sm:flex-row gap-4 items-center justify-center">
                            <Link
                                to="/residential"
                                className="inline-flex items-center gap-2 text-xs tracking-[0.35em] uppercase border border-brand-gold bg-brand-gold text-brand-jet px-8 py-4 hover:bg-transparent hover:text-brand-gold transition-all duration-500"
                                data-testid="hero-cta-residential"
                            >
                                View Collection <ArrowRight size={14} />
                            </Link>
                            <Link
                                to="/contact"
                                className="inline-flex items-center gap-2 text-xs tracking-[0.35em] uppercase border border-brand-silver/30 text-brand-silver px-8 py-4 hover:border-brand-gold hover:text-brand-gold transition-all duration-500"
                                data-testid="hero-cta-consult"
                            >
                                Private Consultation
                            </Link>
                        </div>
                    </Reveal>
                </div>

                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-brand-gold/70 text-[0.6rem] tracking-[0.55em] uppercase flex flex-col items-center gap-2">
                    <span>Scroll</span>
                    <div className="w-px h-10 bg-gradient-to-b from-brand-gold/70 to-transparent" />
                </div>
            </section>

            {/* MARQUEE */}
            <section className="py-10 border-y border-white/5 overflow-hidden bg-brand-onyx/60">
                <div className="marquee-track flex gap-16 whitespace-nowrap text-[0.65rem] tracking-[0.55em] uppercase text-brand-silver/50">
                    {[...Array(2)].map((_, j) => (
                        <div key={j} className="flex gap-16">
                            {[
                                "Sotheby's Trained",
                                "$3.4B Closed",
                                "Nevada Brokerage #BX-92410",
                                "Family Office Network",
                                "Private Client Services",
                                "Off-Market Specialists",
                                "UHNW Concierge",
                            ].map((t, i) => (
                                <span key={`${j}-${i}`} className="flex items-center gap-16">
                                    {t}
                                    <span className="w-1.5 h-1.5 bg-brand-gold rotate-45" />
                                </span>
                            ))}
                        </div>
                    ))}
                </div>
            </section>

            {/* INTRO */}
            <section className="py-28 md:py-36">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-12 gap-12 items-center">
                    <div className="md:col-span-5 md:sticky md:top-32">
                        <Reveal>
                            <p className="text-[0.7rem] tracking-[0.45em] uppercase text-brand-gold mb-6">
                                The House of
                            </p>
                            <h2 className="font-serif-editorial text-4xl md:text-5xl lg:text-6xl text-brand-silver leading-[1.05]">
                                A brokerage built for those who don't list.
                            </h2>
                        </Reveal>
                    </div>
                    <div className="md:col-span-7">
                        <Reveal delay={150}>
                            <p className="text-brand-silver/75 leading-relaxed text-base md:text-lg">
                                Real Estate &amp; Administration Inc represents commercial institutions and
                                private households in the highest tier of the Las Vegas market. We operate
                                quietly — brokering trophy estates, Class-A towers, hospitality, and
                                generational holdings away from the open market.
                            </p>
                            <p className="text-brand-silver/60 leading-relaxed mt-6">
                                Every engagement is personal. Every transaction, administered end-to-end.
                                From first walkthrough to post-close stewardship, our clients receive
                                the standard of care that defines the house.
                            </p>
                            <Link
                                to="/about"
                                className="mt-10 inline-flex items-center gap-2 text-xs tracking-[0.35em] uppercase text-brand-gold border-b border-brand-gold/40 hover:border-brand-gold pb-1 transition"
                                data-testid="home-about-link"
                            >
                                The Firm <ArrowUpRight size={14} />
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>

            <GoldDivider />

            {/* FEATURED */}
            <section className="py-28 md:py-36 bg-brand-jet">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
                        <div>
                            <p className="text-[0.7rem] tracking-[0.45em] uppercase text-brand-gold mb-4">
                                The Collection
                            </p>
                            <h2 className="font-serif-editorial text-4xl md:text-5xl lg:text-6xl text-brand-silver">
                                Featured Properties
                            </h2>
                        </div>
                        <Link
                            to="/residential"
                            className="text-xs tracking-[0.35em] uppercase text-brand-gold hover:text-brand-champagne transition flex items-center gap-2"
                            data-testid="home-view-all-properties"
                        >
                            View all <ArrowUpRight size={14} />
                        </Link>
                    </div>
                    <div className="grid md:grid-cols-12 gap-8">
                        {featured.map((p, i) => (
                            <Reveal key={p.id} delay={i * 120} className={i === 0 ? "md:col-span-8" : "md:col-span-4"}>
                                <PropertyCard p={p} featured={i === 0} />
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* PARALLAX QUOTE */}
            <section
                className="relative py-40 bg-fixed-cover"
                style={{
                    backgroundImage:
                        "url(https://images.unsplash.com/photo-1765323337815-1b1c1a47cdaa?auto=format&fit=crop&w=1800&q=80)",
                }}
            >
                <div className="absolute inset-0 bg-black/70" />
                <div className="relative max-w-4xl mx-auto text-center px-6">
                    <Reveal>
                        <p className="text-[0.65rem] tracking-[0.55em] uppercase text-brand-gold mb-8">
                            Private Client Services
                        </p>
                        <h2 className="font-serif-editorial text-4xl md:text-5xl lg:text-6xl text-brand-champagne italic leading-tight">
                            &ldquo;The market you see is not the market we broker.&rdquo;
                        </h2>
                        <div className="mt-10 text-brand-silver/70 text-sm tracking-[0.35em] uppercase">
                            — The Partners
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* SERVICES */}
            <section className="py-28 md:py-36">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center max-w-3xl mx-auto mb-20">
                        <Reveal>
                            <p className="text-[0.7rem] tracking-[0.45em] uppercase text-brand-gold mb-5">
                                Capabilities
                            </p>
                            <h2 className="font-serif-editorial text-4xl md:text-5xl lg:text-6xl text-brand-silver">
                                A full-service house for the few.
                            </h2>
                        </Reveal>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-brand-gold/10 border border-brand-gold/10">
                        {services.map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <Reveal key={s.id} delay={i * 90} className="bg-brand-jet">
                                    <div className="p-10 lg:p-12 h-full group hover:bg-brand-onyx transition-colors duration-500">
                                        <Icon size={28} strokeWidth={1} className="text-brand-gold" />
                                        <h3 className="mt-6 font-serif-editorial text-2xl text-brand-silver group-hover:text-brand-champagne transition">
                                            {s.name}
                                        </h3>
                                        <p className="mt-4 text-sm text-brand-silver/65 leading-relaxed">
                                            {s.blurb}
                                        </p>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* PREVIEWS */}
            <section className="py-28 md:py-36 bg-brand-onyx/50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 space-y-24">
                    <div>
                        <div className="flex items-end justify-between mb-12">
                            <div>
                                <p className="text-[0.7rem] tracking-[0.45em] uppercase text-brand-gold mb-3">Commercial</p>
                                <h3 className="font-serif-editorial text-3xl md:text-4xl text-brand-silver">Institutional &amp; Trophy Assets</h3>
                            </div>
                            <Link to="/commercial" className="hidden md:inline-flex text-xs tracking-[0.3em] uppercase text-brand-gold items-center gap-2" data-testid="home-commercial-link">
                                Explore <ArrowUpRight size={14} />
                            </Link>
                        </div>
                        <div className="grid md:grid-cols-3 gap-8">
                            {commercial.map((p, i) => (
                                <Reveal key={p.id} delay={i * 100}>
                                    <PropertyCard p={p} />
                                </Reveal>
                            ))}
                        </div>
                    </div>
                    <div>
                        <div className="flex items-end justify-between mb-12">
                            <div>
                                <p className="text-[0.7rem] tracking-[0.45em] uppercase text-brand-gold mb-3">Residential</p>
                                <h3 className="font-serif-editorial text-3xl md:text-4xl text-brand-silver">Multi-Million Dollar Estates</h3>
                            </div>
                            <Link to="/residential" className="hidden md:inline-flex text-xs tracking-[0.3em] uppercase text-brand-gold items-center gap-2" data-testid="home-residential-link">
                                Explore <ArrowUpRight size={14} />
                            </Link>
                        </div>
                        <div className="grid md:grid-cols-3 gap-8">
                            {residential.map((p, i) => (
                                <Reveal key={p.id} delay={i * 100}>
                                    <PropertyCard p={p} />
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-32 md:py-40">
                <div className="max-w-4xl mx-auto text-center px-6">
                    <Reveal>
                        <GoldDivider className="mb-12" />
                        <p className="font-cursive text-brand-gold text-5xl md:text-6xl wordmark-glow leading-none">
                            By appointment only.
                        </p>
                        <p className="mt-8 text-brand-silver/75 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
                            Our client list is private. Our process, invitation-based. Request a consultation
                            and a partner will respond personally within twenty-four hours.
                        </p>
                        <Link
                            to="/contact"
                            className="mt-12 inline-flex items-center gap-3 text-xs tracking-[0.35em] uppercase border border-brand-gold text-brand-gold px-10 py-5 hover:bg-brand-gold hover:text-brand-jet transition-all duration-500"
                            data-testid="home-cta-appointment"
                        >
                            Request Consultation <ArrowRight size={14} />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
    );
};

export default Home;
